/**
 * /pos — staff-facing till (POS terminal) page.
 *
 * Page state machine:
 *   1. Resolve active facility from me.activeFacility || me.facilitiesInScope[0].
 *   2. GET /api/pos/sessions/current?facility_id=<active> → session or null.
 *   3. No session → <OpenTillModal />.
 *   4. Session open → till layout (top bar + stub columns for cart / product grid).
 *
 * Gate: POS is Pro+. Wrapped in <RequireFeature flag="pos">.
 */

import { useAuthContext } from '@/auth/useAuthContext';
import { RequireFeature } from '@/components/RequireFeature';
import {
  ApiError,
  type TipSettings,
  broadcastPosCart,
  closePosSession,
  getCurrentPosSession,
  getTipSettings,
  openPosSession,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { Facility, PosSession } from '@/lib/types';
import { Monitor, ShoppingCart, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PosCart } from './PosCart';
import { PosProductGrid } from './PosProductGrid';
import { PosTenderModal } from './PosTenderModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CartItem {
  kind: 'product' | 'service' | 'manual';
  posProductId?: string;
  serviceId?: string;
  description: string;
  quantity: number;
  unitPriceMinor: number;
  taxable: boolean;
  position: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

// ---------------------------------------------------------------------------
// OpenTillModal
// ---------------------------------------------------------------------------

interface OpenTillModalProps {
  facilityName: string;
  onOpen: (floatMinor: number) => Promise<void>;
}

function OpenTillModal({ facilityName, onOpen }: OpenTillModalProps) {
  const [floatInput, setFloatInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = Math.round(Number.parseFloat(floatInput || '0') * 100);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Enter a valid opening float (e.g. 50.00)');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onOpen(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open till. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      data-testid="open-till-modal"
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-xl ring-1 ring-border">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <ShoppingCart className="size-5 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium tracking-tight">Open till</h2>
            <p className="text-sm text-muted-foreground">{facilityName}</p>
          </div>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-5"
          data-testid="open-till-form"
        >
          <div>
            <label htmlFor="opening-float" className="mb-1.5 block text-sm font-medium">
              Opening float
            </label>
            <div className="flex items-center rounded-lg border border-border bg-background px-3">
              <span className="mr-1 text-sm text-muted-foreground">£</span>
              <input
                id="opening-float"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={floatInput}
                onChange={(e) => setFloatInput(e.target.value)}
                className="w-full bg-transparent py-2 text-sm outline-none num"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Count the cash in the drawer before opening.
            </p>
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Open till'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CloseSessionModal
// ---------------------------------------------------------------------------

interface CloseSessionModalProps {
  session: PosSession;
  currency: string;
  onClose: () => void;
  onConfirm: (floatMinor: number, notes: string) => Promise<void>;
}

function CloseSessionModal({ onClose, onConfirm }: CloseSessionModalProps) {
  const [floatInput, setFloatInput] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = Math.round(Number.parseFloat(floatInput || '0') * 100);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Enter a valid closing float.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(parsed, notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not close session. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-xl ring-1 ring-border">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="font-heading text-lg font-medium">Close session</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Cancel"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-5"
          data-testid="close-session-form"
        >
          <div>
            <label htmlFor="closing-float" className="mb-1.5 block text-sm font-medium">
              Closing float
            </label>
            <div className="flex items-center rounded-lg border border-border bg-background px-3">
              <span className="mr-1 text-sm text-muted-foreground">£</span>
              <input
                id="closing-float"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={floatInput}
                onChange={(e) => setFloatInput(e.target.value)}
                className="w-full bg-transparent py-2 text-sm outline-none num"
              />
            </div>
          </div>

          <div>
            <label htmlFor="close-notes" className="mb-1.5 block text-sm font-medium">
              Notes <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="close-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any discrepancies or comments…"
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-primary"
            />
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-destructive py-2.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Closing…' : 'Close & reconcile'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Till body (session open)
// ---------------------------------------------------------------------------

interface TillBodyProps {
  session: PosSession;
  facility: Facility;
  currency: string;
  onRequestClose: () => void;
}

function TillBody({ session, facility, currency, onRequestClose }: TillBodyProps) {
  const { state: authState } = useAuthContext();
  const account = authState.me?.activeAccount ?? null;
  const [cart, setCart] = useState<CartItem[]>([]);
  // Throttle cart broadcasts so a rapid string of quantity bumps coalesces to
  // one POST every ~250ms. The customer display still sees the running total
  // crisply; we just don't hammer the API on every keystroke.
  const broadcastTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (broadcastTimerRef.current !== null) {
      window.clearTimeout(broadcastTimerRef.current);
    }
    broadcastTimerRef.current = window.setTimeout(() => {
      void broadcastPosCart({
        session_id: session.id,
        kind: 'cart',
        cart_lines: cart.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit_price_minor: l.unitPriceMinor,
        })),
        transaction_id: null,
      }).catch(() => {
        /* broadcast is best-effort; never block till UX */
      });
    }, 250);
    return () => {
      if (broadcastTimerRef.current !== null) {
        window.clearTimeout(broadcastTimerRef.current);
      }
    };
  }, [cart, session.id]);

  function handleOpenCustomerDisplay() {
    const url = `/pos/customer-display?session_id=${encodeURIComponent(session.id)}`;
    window.open(url, 'pos-customer-display', 'noopener,noreferrer,popup=yes,width=1024,height=768');
  }
  const [attachedCustomer, setAttachedCustomer] = useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const [attachedBooking, setAttachedBooking] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [tenderOpen, setTenderOpen] = useState(false);

  // Migration 0098 — per-account tip settings for the card tender step.
  // Defensive: a failed fetch (or a test mock without the endpoint) simply
  // leaves tips off.
  const [tipSettings, setTipSettings] = useState<TipSettings | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTipSettings();
        if (!cancelled) setTipSettings(res.settings);
      } catch {
        // tips stay off
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleAddProduct(product: {
    id: string;
    name: string;
    priceMinor: number;
    taxable: number;
  }) {
    setCart((prev) => {
      const existing = prev.findIndex((l) => l.kind === 'product' && l.posProductId === product.id);
      if (existing !== -1) {
        return prev.map((l, i) => (i === existing ? { ...l, quantity: l.quantity + 1 } : l));
      }
      return [
        ...prev,
        {
          kind: 'product',
          posProductId: product.id,
          description: product.name,
          quantity: 1,
          unitPriceMinor: product.priceMinor,
          taxable: product.taxable === 1,
          position: prev.length,
        },
      ];
    });
  }

  function handleUpdateLine(index: number, patch: Partial<CartItem>) {
    setCart((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  function handleRemoveLine(index: number) {
    setCart((prev) => prev.filter((_, i) => i !== index));
  }

  function handleAddCustomLine() {
    setCart((prev) => [
      ...prev,
      {
        kind: 'manual',
        description: '',
        quantity: 1,
        unitPriceMinor: 0,
        taxable: true,
        position: prev.length,
      },
    ]);
  }

  function handleTenderComplete(_transactionId: string) {
    // Reset cart after successful tender
    setCart([]);
    setAttachedCustomer(null);
    setAttachedBooking(null);
    setTenderOpen(false);
  }

  return (
    <>
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border bg-surface px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex size-2.5 rounded-full bg-success" />
          <div>
            <span className="text-sm font-medium">Session open</span>
            <span className="ml-2 text-sm text-muted-foreground">
              Opened {formatRelativeTime(session.openedAt)} with opening float{' '}
              <span className="num font-medium">
                {formatPriceMinor(session.openingFloatMinor, currency)}
              </span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenCustomerDisplay}
            className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary press"
            data-testid="open-customer-display-btn"
          >
            <Monitor className="size-3.5" strokeWidth={1.75} />
            Customer display
          </button>
          <button
            type="button"
            onClick={onRequestClose}
            className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive press"
            data-testid="close-session-btn"
          >
            Close session
          </button>
        </div>
      </div>

      {/* Till layout: 3-col on lg, stacked below */}
      <div className="flex h-[calc(100dvh-57px)] flex-col lg:flex-row">
        {/* Product grid — left 2/3 */}
        <div className="flex-1 overflow-y-auto border-b border-border lg:border-b-0 lg:border-r">
          <PosProductGrid
            onAddProduct={(product) =>
              handleAddProduct({
                id: product.id,
                name: product.name,
                priceMinor: product.priceMinor,
                taxable: product.taxable,
              })
            }
          />
        </div>

        {/* Cart — right 1/3 */}
        <div className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
          <PosCart
            cart={cart}
            onUpdateLine={handleUpdateLine}
            onRemoveLine={handleRemoveLine}
            onAddCustomLine={handleAddCustomLine}
            customer={attachedCustomer}
            onAttachCustomer={setAttachedCustomer}
            booking={attachedBooking}
            onAttachBooking={setAttachedBooking}
            onTender={() => setTenderOpen(true)}
            currency={currency}
          />
        </div>
      </div>

      {/* Tender modal */}
      {tenderOpen && (
        <PosTenderModal
          cart={cart}
          sessionId={session.id}
          customerId={attachedCustomer?.id ?? null}
          bookingId={attachedBooking?.id ?? null}
          currency={currency}
          facilityName={facility.name}
          accountName={account?.name ?? facility.name}
          taxLabel={account?.taxLabel ?? null}
          taxRegistrationNumber={account?.taxRegistrationNumber ?? null}
          tipSettings={tipSettings}
          customerFirstName={attachedCustomer?.firstName ?? null}
          onComplete={handleTenderComplete}
          onCancel={() => setTenderOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner component (gated by RequireFeature)
// ---------------------------------------------------------------------------

function TillPageBody() {
  const { state } = useAuthContext();

  // Resolve active facility: activeFacility || first in scope
  const facility = state.me?.activeFacility ?? state.me?.facilitiesInScope[0] ?? null;

  const currency = state.me?.activeAccount?.currency ?? 'GBP';

  const [session, setSession] = useState<PosSession | null | undefined>(undefined); // undefined = loading
  const [error, setError] = useState<string | null>(null);
  const [closeModalOpen, setCloseModalOpen] = useState(false);

  const loadSession = useCallback(async () => {
    if (!facility) return;
    try {
      const res = await getCurrentPosSession(facility.id);
      setSession(res.session);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('You do not have permission to access the POS till.');
      } else {
        setError('Could not load session. Please refresh.');
      }
      setSession(null);
    }
  }, [facility]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  async function handleOpenTill(floatMinor: number) {
    if (!facility) return;
    const res = await openPosSession({
      facility_id: facility.id,
      opening_float_minor: floatMinor,
    });
    setSession(res.session);
  }

  async function handleCloseSession(floatMinor: number, notes: string) {
    if (!session) return;
    await closePosSession(session.id, {
      closing_float_minor: floatMinor,
      ...(notes ? { notes } : {}),
    });
    setCloseModalOpen(false);
    // Reload — session is now null (closed)
    await loadSession();
  }

  if (!facility) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <p className="text-sm text-muted-foreground">No facility available.</p>
      </div>
    );
  }

  if (session === undefined) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center px-6">
        <p className="max-w-md rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      </div>
    );
  }

  return (
    <>
      {session === null && <OpenTillModal facilityName={facility.name} onOpen={handleOpenTill} />}

      {session !== null && (
        <TillBody
          session={session}
          facility={facility}
          currency={currency}
          onRequestClose={() => setCloseModalOpen(true)}
        />
      )}

      {closeModalOpen && session !== null && (
        <CloseSessionModal
          session={session}
          currency={currency}
          onClose={() => setCloseModalOpen(false)}
          onConfirm={handleCloseSession}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PosTillPage() {
  return (
    <RequireFeature flag="pos">
      <TillPageBody />
    </RequireFeature>
  );
}
